import React, { ReactNode, createContext, useContext, useEffect, useState } from "react";

type Props = {
    children?: ReactNode;
}

interface User {
    userId: number;
    loginId: string;
    name: string;
    email: string;
    roleId: number;
    roleName: string;
    lastLogin: string;
    // Add other user properties as needed
}

interface AppContextType {
    // User authentication
    user: User | null;
    setUser: (user: User | null) => void;
    
    // Existing functionality
    permissions: any[];
    setPermissions: (permissions: any[]) => void;
    menus: any[];
    setMenus: (menus: any[]) => void;
    userName: string;
    setUserName: (userName: string) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppContextProvider = ({ children }: Props) => {
    // User authentication state
    const [user, setUserState] = useState<User | null>(null);
    
    // Existing state
    const [permissions, setPermissionsData] = useState<any[]>([]);
    const [menus, setMenuData] = useState<any[]>([]);
    const [userName, setUserNameData] = useState<string>('');

    // Load all data from localStorage on initial load
    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        const appPermissions = localStorage.getItem("app-permissions");
        const appMenus = localStorage.getItem("app-menus");
        const appUser = localStorage.getItem("user-name") || 'No User';
        
        if (storedUser) {
            setUserState(JSON.parse(storedUser));
        }
        setPermissionsData(appPermissions !== null ? JSON.parse(appPermissions) : []);
        setMenuData(appMenus !== null ? JSON.parse(appMenus) : []);
        setUserNameData(appUser);
    }, []);


    const handleSetUser = (newUser: User | null) => {
        setUserState(newUser);
        if (newUser) {
            localStorage.setItem('user', JSON.stringify(newUser));
        } else {
            localStorage.removeItem('user');
        }
    };

    // Existing methods
    const setPermissions = (appPermissions: any[]) => {
        console.log('App Context permissions', appPermissions);
        setPermissionsData(appPermissions);
        localStorage.setItem("app-permissions", JSON.stringify(appPermissions));
    };

    const setMenus = (appMenus: any[]) => {
        console.log('App Context menus', appMenus);
        setMenuData(appMenus);
        localStorage.setItem("app-menus", JSON.stringify(appMenus));
    };

    const setUserName = (userName: string) => {
        console.log('App Context user name', userName);
        setUserNameData(userName);
        localStorage.setItem("user-name", userName);
    };

    const contextValue: AppContextType = {
        // User authentication
        user,
        setUser: handleSetUser,
       
        // Existing functionality
        permissions,
        setPermissions,
        menus,
        setMenus,
        userName,
        setUserName
    };

    return (
        <AppContext.Provider value={contextValue}>
            {children}
        </AppContext.Provider>
    );
};

export const useAppContext = () => {
    const context = useContext(AppContext);
    if (context === undefined) {
        throw new Error('useAppContext must be used within an AppProvider');
    }
    return context;
};

